import React, { useState, useContext, createContext, ReactNode } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress } from '@mui/material';

// Define the types of dialogs
type DialogType = 'success' | 'error' | 'info';

interface DialogContextProps {
  showDialog: (type: DialogType, message: string) => void;
}

const DialogContext = createContext<DialogContextProps | undefined>(undefined);

interface DialogProviderProps {
  children: ReactNode;
}

export const DialogProvider: React.FC<DialogProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [dialogType, setDialogType] = useState<DialogType>('success');
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState(100);

  const showDialog = (type: DialogType, msg: string) => {
    setDialogType(type);
    setMessage(msg);
    setOpen(true);
    setProgress(100);
  };

  const handleClose = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    if (open) {
      let timer: NodeJS.Timeout;
      const updateProgress = () => {
        setProgress((oldProgress) => {
          if (oldProgress === 0) {
            handleClose();
            return 100;
          }
          const diff = 100 / 20; // 20 steps for 2 seconds
          return Math.max(oldProgress - diff, 0);
        });
      };
      timer = setInterval(updateProgress, 100);

      return () => {
        clearInterval(timer);
      };
    }
  }, [open]);

  const getTitle = () => {
    switch (dialogType) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      case 'info':
        return 'Info';
      default:
        return '';
    }
  };

  return (
    <DialogContext.Provider value={{ showDialog }}>
      {children}
      <Dialog open={open} onClose={handleClose}>
        <div className="p-4 bg-white rounded-md text-center">
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogContent>
            <p>{message}</p>
            <LinearProgress
              variant="determinate"
              value={progress}
              className="mt-4 h-2 rounded-md"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              OK
            </Button>
          </DialogActions>
        </div>
      </Dialog>
    </DialogContext.Provider>
  );
};

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context.showDialog;
};
